import { config } from '../../../config';
import UserAvatar from '../../components/Header/UserAvatar';
import { __ } from '../../utils';
import type { ProfileSummary } from './useProfileSummary';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { Button, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  summary: ProfileSummary;
};

/**
 * Kto som: koliesko s prstencom úrovne, vedľa neho meno, e-mail a úroveň,
 * a pod tým karta občana.
 *
 * Koliesko je ďalej [[UserAvatar]], nie holý kruh z dizajnového balíka -
 * prstenec v ňom ukazuje postup k ďalšej úrovni a je to jediné miesto
 * v aplikácii, kde ten postup vidno.
 *
 * **Karta občana je primárna akcia bloku**, nie tichá pätička pásu. Je to
 * jediná vec, ktorá z profilu vedie von k účtu na auth-serveri (meno, e-mail
 * aj heslo sú jeho, nie naše), takže patrí k identite a nie na spodnú hranu,
 * kde na ňu občan po odrolovaní sekcií nenarazí.
 */
const ProfileIdentity: React.FC<Props> = ({ summary }) => {
  const tierLine = [
    summary.tier,
    summary.memberSince && `${__('občan od')} ${summary.memberSince}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Stack gap={1.5}>
      <Stack direction='row' alignItems='center' gap={1.5}>
        <UserAvatar />

        <Stack gap={0.25} sx={{ minWidth: 0 }}>
          {!!summary.fullName && (
            <Typography variant='h5' lineHeight={1.2}>
              {summary.fullName}
            </Typography>
          )}
          {!!summary.email && (
            <Typography variant='caption' color='text.secondary' noWrap>
              {summary.email}
            </Typography>
          )}
          {!!tierLine && (
            // Zlatá je farba úrovne, nie stavu - preto `yellow.extraDark`
            // z palety a nie `warning`, ktorá v systéme znamená „duplikát".
            <Typography
              variant='caption'
              fontWeight={700}
              sx={{ color: 'yellow.extraDark' }}
            >
              {tierLine}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Button
        fullWidth
        variant='primary'
        endIcon={<OpenInNew />}
        component='a'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({
          href: config.AUTH_SERVER,
          target: '_blank',
          rel: 'noreferrer',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)} // AnchorProps
      >
        {__('Karta občana')}
      </Button>
    </Stack>
  );
};

export default ProfileIdentity;
